// Client-side route protection for paid signup
import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';

const RouteProtector = ({ children, requiresSession = false }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(requiresSession);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!requiresSession) {
      setIsValidating(false);
      return;
    }

    const sessionId = searchParams.get('session_id');
    
    if (!sessionId) {
      navigate('/subscription-required', { replace: true });
      return;
    }

    // Validate session on client side as well
    validateSession(sessionId);
  }, [searchParams, navigate, requiresSession]);

  const validateSession = async (sessionId) => {
    try {
      const response = await fetch(`/api/stripe/verify-session/${sessionId}`);
      
      if (!response.ok) {
        const errorData = await response.json();
        
        if (response.status === 403) {
          // Handle specific security errors
          switch (errorData.errorType) {
            case 'EXPIRED':
              navigate('/subscription-required?error=session_expired', { replace: true });
              break;
            case 'ALREADY_USED':
              navigate('/login?error=session_used', { replace: true });
              break;
            case 'TOO_MANY_ATTEMPTS':
              navigate('/subscription-required?error=too_many_attempts', { replace: true });
              break;
            default:
              navigate('/subscription-required?error=invalid_session', { replace: true });
          }
          return;
        }
        
        throw new Error(errorData.message || 'Session validation failed');
      }
      
      setIsValidating(false);
      
    } catch (error) {

      setError(error.message);
      setTimeout(() => {
        navigate('/subscription-required?error=validation_failed', { replace: true });
      }, 3000);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-500">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md mx-4">
          <div className="text-center">
            <div className="text-red-500 text-5xl mb-4">⚠️</div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Session Error</h2>
            <p className="text-gray-600 mb-4">{error}</p>
            <p className="text-sm text-gray-500">Redirecting to pricing page...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-600 to-pink-500">
        <div className="bg-white p-8 rounded-lg shadow-xl max-w-md mx-4">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Validating Session</h2>
            <p className="text-gray-600">Please wait while we verify your payment...</p>
          </div>
        </div>
      </div>
    );
  }

  return children;
};

export default RouteProtector;